import { useEffect, useRef } from "preact/hooks";
import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";
import { SkeletonLoader } from "../SkeletonLoader";
import { ErrorMessage } from "../ErrorMessage";
import { formatBps } from "../../utils/format";
import {
  getChartColors,
  colorWithAlpha,
  isoToUnixSeconds,
  createTooltip,
  makeTimeFormatter,
  formatBpsAxis,
  useChartTheme,
} from "./chartUtils";

type InterfaceChartProps = {
  data: Array<{ timestamp: string; rx_rate: number; tx_rate: number }>;
  loading?: boolean;
  error?: string;
};

export function InterfaceChart({ data, loading, error }: InterfaceChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<uPlot | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const theme = useChartTheme();

  useEffect(() => {
    if (loading || error || data.length === 0) return;
    if (!containerRef.current) return;

    const colors = getChartColors();
    const tooltip = createTooltip();
    tooltipRef.current = tooltip;
    containerRef.current.appendChild(tooltip);

    const timestamps = data.map((d) => isoToUnixSeconds(d.timestamp));
    const rx = data.map((d) => d.rx_rate);
    const tx = data.map((d) => d.tx_rate);

    const width = containerRef.current.clientWidth;
    const height = 300;

    const opts: uPlot.Options = {
      width,
      height,
      cursor: { drag: { x: false, y: false } },
      legend: { show: true },
      axes: [
        {
          stroke: colors.text,
          grid: { stroke: colors.grid },
          ticks: { stroke: colors.grid },
          values: makeTimeFormatter(timestamps),
        },
        {
          stroke: colors.text,
          grid: { stroke: colors.grid },
          ticks: { stroke: colors.grid },
          values: formatBpsAxis,
          size: 70,
        },
      ],
      series: [
        {},
        {
          label: "Download (RX)",
          stroke: colors.blue,
          fill: colorWithAlpha(colors.blue, 0.15),
          width: 2,
        },
        {
          label: "Upload (TX)",
          stroke: colors.purple,
          fill: colorWithAlpha(colors.purple, 0.15),
          width: 2,
        },
      ],
      hooks: {
        setCursor: [
          (u) => {
            const idx = u.cursor.idx;
            if (idx == null || idx < 0) {
              tooltip.style.display = "none";
              return;
            }
            const t = u.data[0][idx];
            const rxVal = u.data[1][idx];
            const txVal = u.data[2][idx];
            if (t == null || rxVal == null || txVal == null) {
              tooltip.style.display = "none";
              return;
            }
            tooltip.innerHTML = `
              <div><strong>${new Date(t * 1000).toLocaleString("id-ID")}</strong></div>
              <div style="color:${colors.blue}">Download (RX): ${formatBps(rxVal)}</div>
              <div style="color:${colors.purple}">Upload (TX): ${formatBps(txVal)}</div>
            `;
            tooltip.style.display = "block";
            tooltip.style.left = `${(u.cursor.left ?? 0) + 12}px`;
            tooltip.style.top = `${(u.cursor.top ?? 0) + 12}px`;
          },
        ],
      },
    };

    const chart = new uPlot(opts, [timestamps, rx, tx], containerRef.current);
    chartRef.current = chart;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        chart.setSize({ width: entry.contentRect.width, height });
      }
    });
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
      chart.destroy();
      tooltip.remove();
      chartRef.current = null;
      tooltipRef.current = null;
    };
  }, [data, theme, loading, error]);

  if (loading) return <SkeletonLoader type="chart" />;
  if (error) return <ErrorMessage message={error} />;
  if (data.length === 0) return <div class="chart-empty">Belum ada data</div>;

  return <div ref={containerRef} class="chart-container" />;
}
